/**
 * Fenster-Identität: WAS ein Fenster zeigt (#850).
 *
 * Das Fenstersystem erlaubt beliebig viele Instanzen derselben App. Für die
 * meisten Apps ist das richtig — zwei Einstellungsfenster stören niemanden.
 * Zeigt ein Fenster aber ein bestimmtes OBJEKT, ist eine zweite Instanz darauf
 * schädlich: zwei Editoren auf demselben Transkript überschreiben sich
 * gegenseitig, und welcher gewinnt, entscheidet die Reihenfolge der Klicks.
 *
 * `openWindow` hatte dafür bereits eine Deduplizierung, aber fest verdrahtet
 * auf `documentId`. Diese Datei verallgemeinert die Frage: Ein Parameter aus
 * `WINDOW_IDENTITY_PARAMS` macht aus einem Fenster „die App" ein Fenster
 * „dieses Objekt in der App". Zwei Öffnungen mit gleicher App und gleicher
 * Identität sind dasselbe Fenster.
 *
 * Neue Parameter gehören NUR dann in die Liste, wenn ein zweites Fenster auf
 * dasselbe Objekt tatsächlich falsch wäre. Ein Filter oder ein aktiver Tab
 * identifiziert nichts — daran zwei Fenster festzumachen wäre legitim.
 */

import type { WindowParams } from '@/types/window'

/**
 * Parameter, die den Gegenstand eines Fensters benennen, in Prüfreihenfolge.
 *
 * Nur Langformen: `normalizeWindowParams` löst Kurz-Aliasse (`d`, `f`, …) über
 * `resolveDeepLinkParamKey` auf, bevor die Identität bestimmt wird.
 */
export const WINDOW_IDENTITY_PARAMS = ['documentId', 'recordingId'] as const

export type WindowIdentityParam = (typeof WINDOW_IDENTITY_PARAMS)[number]

/**
 * Schlüssel, unter dem ein Fenster wiedergefunden wird — oder `null`, wenn das
 * Fenster kein bestimmtes Objekt zeigt und deshalb beliebig oft offen sein darf.
 *
 * Der Parametername steht mit im Schlüssel, damit eine `documentId` und eine
 * `recordingId` mit demselben Wert nicht auf dasselbe Fenster zeigen. Beides
 * sind UUIDs aus verschiedenen Tabellen; dass sie sich nie gleichen, ist eine
 * Annahme, die dieser Schlüssel nicht braucht.
 */
export function windowIdentityKey(appId: string, params?: WindowParams): string | null {
  if (!params) return null

  for (const key of WINDOW_IDENTITY_PARAMS) {
    const value = params[key]
    if (typeof value === 'string' && value.length > 0) {
      return `${appId}:${key}:${value}`
    }
  }

  return null
}

/** What `reusableWindow` needs to know about a window. */
export interface ReusableWindowCandidate {
  appId: string
  params?: WindowParams
  zIndex: number
}

/**
 * Das Fenster, das ein Ziel ohne eigene Identität wiederverwenden soll: das
 * vorderste Fenster derselben App, das kein bestimmtes Objekt zeigt — oder
 * keins (#1069, #1188).
 *
 * Benennt das Ziel selbst ein Objekt (`documentId`, `recordingId`), gibt es
 * hier nichts wiederzuverwenden: Ein fremdes Dokumentfenster umzubiegen,
 * tauschte aus, woran jemand gerade arbeitet. Solche Ziele findet die
 * Identitätsprüfung (`windowIdentityKey`).
 */
export function reusableWindow<W extends ReusableWindowCandidate>(
  windows: W[],
  appId: string,
  params?: WindowParams,
): W | undefined {
  if (windowIdentityKey(appId, params)) return undefined
  return windows
    .filter((w) => w.appId === appId && !windowIdentityKey(w.appId, w.params))
    .reduce<W | undefined>((best, w) => (!best || w.zIndex > best.zIndex ? w : best), undefined)
}
